// libs
import React, { useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';

// constants
import { ARIA_LABELS } from 'shared/layouts/Coupons/constants';

// components
import { OverlaySheetContext, SHEET_TRANSITION } from 'shared/components/OverlaySheet';
import ValidityText from 'shared/layouts/Coupons/CouponsExpiry/CouponValdity/CouponValidity';

// hooks
import useKeyboardClick from 'shared/hooks/useKeyboardClick';

// icons
import { useStoreIcon, IconName } from 'shared/icons';

// context
import { useLoaderContext } from 'shared/components/LoaderProvider/context';

// styles
import {
  Header,
  OfferTitle,
  Subtitle,
  InfoContainer,
  CouponInfo,
  BottomContainer,
  ActionButtonOffer,
  ActionContainerPDP,
  DiscountTagWrapper,
  OverlaySheetDesktop,
  IconContainerDesktop as CloseButton,
  FixedContent,
} from './styles';

// constants
import { TEXTS } from 'shared/layouts/Coupons/constants';
import { LOGO_TYPE } from 'shared/constants';

// types
import { OfferDetails } from 'shared/layouts/Coupons/types';
import Logo from 'shared/components/Logo';

// defs
interface ViewDetailsNudgeOfferProps {
  onHide: VoidFunction;
  offerDetails: OfferDetails;
  sheetTransition?: SHEET_TRANSITION;
}

function ViewDetailsNudgeOffer({
  onHide,
  offerDetails,
  sheetTransition = SHEET_TRANSITION.RIGHT,
}: ViewDetailsNudgeOfferProps) {
  const { showLoader, hideLoader } = useLoaderContext();
  const CloseIcon = useStoreIcon(IconName.CLOSE);

  const closeKeyboardProps = useKeyboardClick(onHide);
  const closeButtonRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (closeButtonRef.current) {
      closeButtonRef.current.focus();
    }

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex^="-"])'
      );

      const focusableArray = Array.from(focusableElements);
      if (focusableArray.length === 0) return;

      const firstElement = focusableArray[0];
      const lastElement = focusableArray[focusableArray.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, []);

  if (isEmpty(offerDetails)) {
    showLoader({});
    return null;
  } else hideLoader();

  const { title = '', description = '', expiryEpoch = 0 } = offerDetails || {};

  return (
    <OverlaySheetDesktop onHide={onHide} transitionFrom={sheetTransition} navKey={'offer-dweb'}>
      <OverlaySheetContext.Consumer>
        {({ doHide }) => (
          <FixedContent ref={modalRef}>
            <Header>
              <CloseButton
                ref={closeButtonRef}
                {...closeKeyboardProps}
                aria-label={ARIA_LABELS.CLOSE_OFFER_DETAILS}
              >
                <CloseIcon aria-hidden="true" />
              </CloseButton>
            </Header>

            <InfoContainer className="infoContainer">
              <CouponInfo>
                <DiscountTagWrapper>
                  <Logo type={LOGO_TYPE.OFFERS} />
                </DiscountTagWrapper>
                <OfferTitle>{title}</OfferTitle>
                <Subtitle>{description}</Subtitle>
                {expiryEpoch > 0 && <ValidityText expiryEpoch={expiryEpoch} />}
              </CouponInfo>
            </InfoContainer>
            <BottomContainer showTopBorder={false} isDesktop={true}>
              <ActionContainerPDP>
                <ActionButtonOffer
                  kind={'secondary'}
                  size={'medium'}
                  shape={'default'}
                  onClick={onHide}
                >
                  {TEXTS.OKAY}
                </ActionButtonOffer>
              </ActionContainerPDP>
            </BottomContainer>
          </FixedContent>
        )}
      </OverlaySheetContext.Consumer>
    </OverlaySheetDesktop>
  );
}

export default ViewDetailsNudgeOffer;
